import React, { createContext, useContext, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBrowseChromeCompact } from '../hooks/useBrowseChromeCompact';
import {
    BROWSE_CHROME_MORPH_MS,
    compensateBrowseChromeMorph,
    takeBrowseChromeMorphAnchor,
    unlockBrowseChromeMorph,
} from '../hooks/browseChromeMorphUtils';

const BrowseChromeCompactContext = createContext(false);
const MORPHING_CLASS = 'browse-chrome-morphing';

function isBrowseChromeRoute(pathname) {
    return pathname === '/' || pathname === '/products';
}

/**
 * Single scroll-compact listener for Home + Product Catalog. Sets html classes
 * in useLayoutEffect (same frame as Navbar) and shares compact state via context.
 */
export function BrowseChromeCompactProvider({ children }) {
    const { pathname } = useLocation();
    const enabled = isBrowseChromeRoute(pathname);
    const compact = useBrowseChromeCompact(enabled);
    const prevEnabledRef = useRef(false);
    const morphTimerRef = useRef(null);
    const pendingAnchorRef = useRef(null);

    useLayoutEffect(() => {
        const root = document.documentElement;
        root.classList.toggle('browse-chrome-route', enabled);
        root.classList.toggle('browse-chrome-compact', enabled && compact);

        const anchor = takeBrowseChromeMorphAnchor();
        if (enabled && anchor) {
            pendingAnchorRef.current = anchor;
            root.classList.add(MORPHING_CLASS);
            if (morphTimerRef.current) {
                clearTimeout(morphTimerRef.current);
            }
            morphTimerRef.current = window.setTimeout(() => {
                morphTimerRef.current = null;
                root.classList.remove(MORPHING_CLASS);
                const doneAnchor = pendingAnchorRef.current;
                pendingAnchorRef.current = null;
                unlockBrowseChromeMorph();
                if (doneAnchor) {
                    compensateBrowseChromeMorph(doneAnchor);
                }
            }, BROWSE_CHROME_MORPH_MS);
        }

        if (!enabled && prevEnabledRef.current) {
            pendingAnchorRef.current = null;
            root.classList.remove('browse-chrome-compact', 'browse-chrome-scrolling', MORPHING_CLASS);
            unlockBrowseChromeMorph();
        }
        prevEnabledRef.current = enabled;

        return () => {
            if (morphTimerRef.current) {
                clearTimeout(morphTimerRef.current);
                morphTimerRef.current = null;
            }
            pendingAnchorRef.current = null;
            unlockBrowseChromeMorph();
            root.classList.remove('browse-chrome-route', 'browse-chrome-compact', 'browse-chrome-scrolling', MORPHING_CLASS);
        };
    }, [enabled, compact]);

    return (
        <BrowseChromeCompactContext.Provider value={enabled ? compact : false}>
            {children}
        </BrowseChromeCompactContext.Provider>
    );
}

export function useBrowseChromeCompactState() {
    return useContext(BrowseChromeCompactContext);
}
